import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Users, Eye, ArrowLeft, Download, Calendar, Briefcase } from 'lucide-react';
import { useJobApplicants } from 'features/job/hooks/useApplications';
import { useJob } from 'features/job/hooks/useJobs';
import { ViewApplicationModal } from 'features/job/components/ViewApplicationModal';
import { formatDateTime } from 'utils/dateFormatter';
import Layout from 'components/ui/Layout';
import { jobServices } from 'features/job/services/job.service';

const JobApplicants = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();

  // State for filters
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [status, setStatus] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Modal state
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);

  // Fetch job details
  const { data: jobResponse, isLoading: jobLoading } = useJob(jobId);
  const job = jobResponse?.data?.job || jobResponse?.data;

  // Fetch applicants
  const { data: response, isPending, isError, error } = useJobApplicants({
    jobId,
    page,
    limit,
    status: status || undefined,
    sortBy,
    sortOrder,
  });

  const applicants = response?.data?.applicants || [];
  const pagination = response?.data?.pagination || {};

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleView = (application) => {
    setSelectedApplication(application);
    setIsViewModalOpen(true);
  };

  const handleExport = async () => {
  try {
    const response = await jobServices.exportApplicants(jobId, {
      status: status || undefined,
      sortBy,
      order: sortOrder,
    });

    const blob = new Blob([response.data], {
      type: 'text/csv',
    });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `job-applicants-${job?.jobTitle || 'export'}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to export applicants:', error);
  }
};

  const getStatusColor = (status) => {
    const colors = {
      Submitted: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      'Under Review': 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
      Shortlisted: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
      Accepted: 'bg-green-500/10 text-green-600 dark:text-green-400',
      Rejected: 'bg-red-500/10 text-red-600 dark:text-red-400',
    };
    return colors[status] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
  };

  if (isError) {
    return (
      <Layout>
        <div className='flex flex-col items-center justify-center h-screen'>
          <div className='text-destructive text-lg mb-4'>Error loading applicants</div>
          <p className='text-muted-foreground'>{error?.message || 'Something went wrong'}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className='p-6 space-y-6'>
        {/* Header */}
     <div className='flex items-center justify-between'>
  <div>
    <button
      onClick={() => navigate('/jobs')}
      className='flex items-center gap-2 text-muted-foreground hover:text-foreground mb-2 transition-colors'
    >
      <ArrowLeft size={16} />
      Back to Jobs
    </button>

    <h1 className='text-3xl font-bold flex items-center gap-2'>
      <Users className='text-primary' size={32} />
      Job Applicants
    </h1>

    {jobLoading ? (
      <p className='text-muted-foreground mt-1'>Loading job details...</p>
    ) : job ? (
      <div className='mt-2'>
        <p className='text-lg font-semibold text-foreground'>
          {job.jobTitle}
        </p>
        <p className='text-sm text-muted-foreground'>
          Posted by: {job.sellerId?.firstName} {job.sellerId?.lastName}
        </p>
      </div>
    ) : null}
  </div>

  <button
    onClick={handleExport}
    className='flex items-center space-x-2 px-4 py-2 text-sm border border-border rounded-md bg-card text-card-foreground hover:bg-accent transition-colors'
  >
    <Download size={16} />
     <span>Export</span>
  </button>
</div>

        {/* Stats */}
        {pagination && (
          <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
            <div className='bg-card border border-border rounded-lg p-4'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-muted-foreground'>Total Applications</p>
                  <p className='text-2xl font-bold text-foreground'>{pagination.total || 0}</p>
                </div>
                <Users className='text-blue-500' size={24} />
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className='bg-card border border-border rounded-lg p-4'>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            {/* Status Filter */}
            <div>
              <label className='block text-sm font-medium text-foreground mb-2'>Status</label>
              <select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
                className='w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary'
              >
                <option value=''>All Status</option>
                <option value='Submitted'>Submitted</option>
                <option value='Under Review'>Under Review</option>
                <option value='Shortlisted'>Shortlisted</option>
                <option value='Accepted'>Accepted</option>
                <option value='Rejected'>Rejected</option>
              </select>
            </div>

            {/* Sort By */}
            <div>
              <label className='block text-sm font-medium text-foreground mb-2'>Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className='w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary'
              >
                <option value='createdAt'>Application Date</option>
                <option value='fullName'>Applicant Name</option>
                <option value='status'>Status</option>
              </select>
            </div>

            {/* Sort Order */}
            <div>
              <label className='block text-sm font-medium text-foreground mb-2'>Order</label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className='w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary'
              >
                <option value='desc'>Newest First</option>
                <option value='asc'>Oldest First</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className='bg-card border border-border rounded-lg overflow-hidden'>
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead className='bg-muted/50 border-b border-border'>
                <tr>
                  <th className='text-left px-4 py-3 text-sm font-semibold text-foreground'>Applicant</th>
                  <th className='text-left px-4 py-3 text-sm font-semibold text-foreground'>Contact</th>
                  <th className='text-left px-4 py-3 text-sm font-semibold text-foreground'>Applied On</th>
                  <th className='text-left px-4 py-3 text-sm font-semibold text-foreground'>Status</th>
                  <th className='text-left px-4 py-3 text-sm font-semibold text-foreground'>Resume</th>
                  <th className='text-left px-4 py-3 text-sm font-semibold text-foreground'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isPending ? (
                  <tr>
                    <td colSpan='6' className='text-center py-8'>
                      <div className='flex items-center justify-center'>
                        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary' />
                      </div>
                    </td>
                  </tr>
                ) : applicants.length === 0 ? (
                  <tr>
                    <td colSpan='6' className='text-center py-8 text-muted-foreground'>
                      No applications found for this job
                    </td>
                  </tr>
                ) : (
                  applicants.map((application) => (
                    <tr key={application._id} className='border-b border-border hover:bg-muted/30 transition-colors'>
                      <td className='px-4 py-3'>
                        <p className='font-medium text-foreground'>{application.firstName || 'N/A'} {application.lastName || 'N/A'}</p>
                      </td>
                      <td className='px-4 py-3'>
                        <div>
                          <p className='text-sm text-foreground'>{application.email || 'N/A'}</p>
                          <p className='text-xs text-muted-foreground'>{application.phone || 'N/A'}</p>
                        </div>
                      </td>
                      <td className='px-4 py-3'>
                        <div className='flex items-center gap-2'>
                          <Calendar size={14} className='text-muted-foreground' />
                          <p className='text-sm text-foreground'>
                            {application.createdAt
                              ? new Date(application.createdAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                })
                              : 'N/A'}
                          </p>
                        </div>
                      </td>
                      <td className='px-4 py-3'>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(application.status)}`}
                        >
                          {application.status}
                        </span>
                      </td>
                      <td className='px-4 py-3'>
                        {application.resumeUrl ? (
                          <a
                            href={application.resumeUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='inline-flex items-center gap-1 text-xs text-primary hover:underline'
                          >
                            <Download size={14} />
                            Download
                          </a>
                        ) : (
                          <span className='text-xs text-muted-foreground'>No resume</span>
                        )}
                      </td>
                      <td className='px-4 py-3'>
                        <div className='flex items-center gap-2'>
                          <button
                            onClick={() => handleView(application)}
                            className='p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-500/10 rounded transition-colors'
                            title='View Details'
                          >
                            <Eye size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className='flex items-center justify-between px-4 py-3 border-t border-border'>
              <div className='text-sm text-muted-foreground'>
                Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} applications
              </div>
              <div className='flex items-center gap-2'>
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className='px-3 py-1 border border-border rounded-md hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                >
                  Previous
                </button>
                <span className='text-sm text-foreground'>
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className='px-3 py-1 border border-border rounded-md hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <ViewApplicationModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        application={selectedApplication}
      />
    </Layout>
  );
};

export default JobApplicants;
